import React, { useState, useRef, useEffect } from 'react';
import Sidebar from '../components/shared/Sidebar';
import rentalUtilizationService, { 
  ImportResult,
  FacilitySummary,
  EquipmentFacilitySummary 
} from '../services/rentalUtilizationService';
import './RentalUtilizationImportPage.css';

type ImportStep = 'upload' | 'importing' | 'complete';
type FileType = 'monthly_utilization' | 'summary' | 'equipment_master' | 'unknown';

interface QueuedFile {
  file: File;
  status: 'pending' | 'importing' | 'success' | 'error';
  detectedType: FileType;
  result?: ImportResult;
  error?: string;
}

const RentalUtilizationImportPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<ImportStep>('upload');
  const [queuedFiles, setQueuedFiles] = useState<QueuedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [facilities, setFacilities] = useState<FacilitySummary[]>([]);
  const [equipmentSummary, setEquipmentSummary] = useState<EquipmentFacilitySummary[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load summary data on mount
  useEffect(() => {
    loadSummaryData();
  }, []);

  const loadSummaryData = async () => {
    try {
      const [facilitiesData, summaryData] = await Promise.all([
        rentalUtilizationService.getFacilities().catch(() => []),
        rentalUtilizationService.getEquipmentSummary().catch(() => [])
      ]);
      setFacilities(facilitiesData);
      setEquipmentSummary(summaryData);
    } catch (err) {
      console.log('Summary data not available yet');
    }
  };

  const detectFileType = (fileName: string): FileType => {
    const nameLower = fileName.toLowerCase();
    
    if (nameLower.includes('equipment') || nameLower.includes('master') || nameLower.includes('units')) {
      return 'equipment_master';
    }
    if (nameLower.includes('monthly') || nameLower.includes('by fac')) {
      return 'monthly_utilization';
    }
    if (nameLower.includes('summary') || nameLower.includes('628')) {
      return 'summary';
    }
    
    return 'unknown';
  };

  const handleFilesSelect = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFiles: QueuedFile[] = [];
    
    for (const file of fileArray) {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setError(`"${file.name}" is not a CSV file`);
        continue;
      }

      if (file.size > 50 * 1024 * 1024) {
        setError(`"${file.name}" exceeds 50MB limit`);
        continue;
      }

      const detectedType = detectFileType(file.name);
      
      validFiles.push({
        file,
        status: 'pending',
        detectedType
      });
    }

    if (validFiles.length > 0) {
      setQueuedFiles(prev => [...prev, ...validFiles]);
      setError(null);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFilesSelect(files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFilesSelect(files);
    }
  };

  const removeFile = (index: number) => {
    setQueuedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleReset = () => {
    setCurrentStep('upload');
    setQueuedFiles([]);
    setError(null);
    setIsImporting(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImport = async () => {
    if (queuedFiles.length === 0) return;

    setIsImporting(true);
    setCurrentStep('importing');

    const updatedFiles = [...queuedFiles];

    for (let i = 0; i < updatedFiles.length; i++) {
      const queuedFile = updatedFiles[i];
      
      updatedFiles[i] = { ...queuedFile, status: 'importing' };
      setQueuedFiles([...updatedFiles]);

      try {
        const result = await rentalUtilizationService.importFromCSV(queuedFile.file);
        
        updatedFiles[i] = {
          ...queuedFile,
          status: result.success ? 'success' : 'error',
          result,
          detectedType: result.fileType || queuedFile.detectedType
        };
      } catch (err: any) {
        updatedFiles[i] = {
          ...queuedFile,
          status: 'error',
          error: err.message || 'Import failed'
        };
      }

      setQueuedFiles([...updatedFiles]);
    }

    setIsImporting(false);
    setCurrentStep('complete');
    
    await loadSummaryData();
  };

  const getFileTypeInfo = (type: FileType) => {
    switch (type) {
      case 'monthly_utilization':
        return { label: 'Monthly Utilization', color: '#3b82f6', icon: '📊' };
      case 'summary':
        return { label: 'Summary', color: '#8b5cf6', icon: '📋' };
      case 'equipment_master':
        return { label: 'Equipment Master', color: '#10b981', icon: '🚛' };
      default:
        return { label: 'Unknown', color: '#6b7280', icon: '❓' };
    }
  };

  const totalUnits = equipmentSummary.reduce((sum, f) => sum + f.totalUnits, 0);
  const totalActiveUnits = equipmentSummary.reduce((sum, f) => sum + f.activeUnits, 0);

  const successCount = queuedFiles.filter(f => f.status === 'success').length;
  const errorCount = queuedFiles.filter(f => f.status === 'error').length;
  const totalCreated = queuedFiles.reduce((sum, f) => sum + (f.result?.data?.created || 0), 0);
  const totalUpdated = queuedFiles.reduce((sum, f) => sum + (f.result?.data?.updated || 0), 0);

  const steps = [
    { key: 'upload', label: 'Select Files', icon: '📁' },
    { key: 'importing', label: 'Processing', icon: '⚡' },
    { key: 'complete', label: 'Complete', icon: '✓' }
  ];

  const getStepStatus = (stepKey: string) => {
    const stepOrder = ['upload', 'importing', 'complete'];
    const currentIndex = stepOrder.indexOf(currentStep);
    const stepIndex = stepOrder.indexOf(stepKey);
    
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'active';
    return 'pending';
  };

  return (
    <div className="rental-utilization-import-page">
      <Sidebar />
      <main className="rental-utilization-content">
        {/* Header */}
        <header className="ru-header">
          <div className="ru-header-content">
            <div className="ru-header-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27,6.96 12,12.01 20.73,6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
            </div>
            <div>
              <h1>Rental Utilization Import</h1>
              <p>Import and manage your rental equipment data</p>
            </div>
          </div>
        </header>

        {/* Stats Bar */}
        {(facilities.length > 0 || equipmentSummary.length > 0) && (
          <div className="ru-stats-bar">
            <div className="ru-stat">
              <div className="ru-stat-icon facilities">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 21h18"/>
                  <path d="M9 8h1"/>
                  <path d="M9 12h1"/>
                  <path d="M9 16h1"/>
                  <path d="M14 8h1"/>
                  <path d="M14 12h1"/>
                  <path d="M14 16h1"/>
                  <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"/>
                </svg>
              </div>
              <div className="ru-stat-content">
                <span className="ru-stat-value">{facilities.length}</span>
                <span className="ru-stat-label">Facilities</span>
              </div>
            </div>
            <div className="ru-stat">
              <div className="ru-stat-icon units">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="1" y="3" width="15" height="13"/>
                  <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                  <circle cx="5.5" cy="18.5" r="2.5"/>
                  <circle cx="18.5" cy="18.5" r="2.5"/>
                </svg>
              </div>
              <div className="ru-stat-content">
                <span className="ru-stat-value">{totalUnits.toLocaleString()}</span>
                <span className="ru-stat-label">Total Units</span>
              </div>
            </div>
            <div className="ru-stat">
              <div className="ru-stat-icon active">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <div className="ru-stat-content">
                <span className="ru-stat-value">{totalActiveUnits.toLocaleString()}</span>
                <span className="ru-stat-label">Active Units</span>
              </div>
            </div>
            <div className="ru-stat">
              <div className="ru-stat-icon records">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
              </div>
              <div className="ru-stat-content">
                <span className="ru-stat-value">{equipmentSummary.length}</span>
                <span className="ru-stat-label">Records</span>
              </div>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div className="ru-progress">
          {steps.map((step, index) => (
            <React.Fragment key={step.key}>
              <div className={`ru-progress-step ${getStepStatus(step.key)}`}>
                <div className="ru-progress-circle">
                  {getStepStatus(step.key) === 'completed' ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  ) : (
                    <span>{step.icon}</span>
                  )}
                </div>
                <span className="ru-progress-label">{step.label}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`ru-progress-line ${getStepStatus(steps[index + 1].key) !== 'pending' ? 'active' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Main Content Area */}
        <div className="ru-main-area">
          {/* Upload Section */}
          {currentStep === 'upload' && (
            <div className="ru-upload-section">
              <div 
                className={`ru-dropzone ${isDragging ? 'dragging' : ''} ${queuedFiles.length > 0 ? 'has-files' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="ru-dropzone-content">
                  <div className="ru-dropzone-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                  </div>
                  <h3>Drop your CSV files here</h3>
                  <p>or click to browse your computer</p>
                  <button className="ru-browse-btn" type="button">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                    </svg>
                    Browse Files
                  </button>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileInputChange}
                  accept=".csv"
                  multiple
                  style={{ display: 'none' }}
                />
              </div>

              {/* Queued Files */}
              {queuedFiles.length > 0 && (
                <div className="ru-queued-files">
                  <div className="ru-queued-header">
                    <h4>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
                        <polyline points="13 2 13 9 20 9"/>
                      </svg>
                      Ready to Import ({queuedFiles.length} file{queuedFiles.length > 1 ? 's' : ''})
                    </h4>
                  </div>
                  <div className="ru-file-list">
                    {queuedFiles.map((qf, index) => {
                      const typeInfo = getFileTypeInfo(qf.detectedType);
                      return (
                        <div key={index} className="ru-file-item">
                          <div className="ru-file-icon" style={{ background: `${typeInfo.color}15`, color: typeInfo.color }}>
                            {typeInfo.icon}
                          </div>
                          <div className="ru-file-details">
                            <span className="ru-file-name">{qf.file.name}</span>
                            <span className="ru-file-meta">
                              <span className="ru-file-type" style={{ color: typeInfo.color }}>{typeInfo.label}</span>
                              <span className="ru-file-size">{(qf.file.size / 1024).toFixed(1)} KB</span>
                            </span>
                          </div>
                          <button 
                            className="ru-file-remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(index);
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18"/>
                              <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <div className="ru-actions">
                    <button className="ru-btn ru-btn-ghost" onClick={handleReset}>
                      Clear All
                    </button>
                    <button className="ru-btn ru-btn-primary" onClick={handleImport}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="16 16 12 12 8 16"/>
                        <line x1="12" y1="12" x2="12" y2="21"/>
                        <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                      </svg>
                      Start Import
                    </button>
                  </div>
                </div>
              )}

              {error && (
                <div className="ru-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {error}
                </div>
              )}

              {/* Supported File Types */}
              <div className="ru-file-types">
                <h4>Supported Import Formats</h4>
                <div className="ru-type-grid">
                  <div className="ru-type-card monthly">
                    <div className="ru-type-header">
                      <div className="ru-type-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="20" x2="18" y2="10"/>
                          <line x1="12" y1="20" x2="12" y2="4"/>
                          <line x1="6" y1="20" x2="6" y2="14"/>
                        </svg>
                      </div>
                      <h5>Monthly Utilization</h5>
                    </div>
                    <p>Time-series utilization metrics by facility and equipment type</p>
                    <div className="ru-type-tags">
                      <span>Report Date</span>
                      <span>Utilization %</span>
                      <span>Revenue</span>
                    </div>
                  </div>
                  <div className="ru-type-card summary">
                    <div className="ru-type-header">
                      <div className="ru-type-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                          <line x1="16" y1="13" x2="8" y2="13"/>
                          <line x1="16" y1="17" x2="8" y2="17"/>
                        </svg>
                      </div>
                      <h5>Utilization Summary</h5>
                    </div>
                    <p>Point-in-time snapshot of facility and equipment performance</p>
                    <div className="ru-type-tags">
                      <span>Facility</span>
                      <span>Equipment Type</span>
                      <span>Metrics</span>
                    </div>
                  </div>
                  <div className="ru-type-card equipment">
                    <div className="ru-type-header">
                      <div className="ru-type-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="1" y="3" width="15" height="13"/>
                          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                          <circle cx="5.5" cy="18.5" r="2.5"/>
                          <circle cx="18.5" cy="18.5" r="2.5"/>
                        </svg>
                      </div>
                      <h5>Equipment Master</h5>
                    </div>
                    <p>Complete unit-level inventory with specifications and status</p>
                    <div className="ru-type-tags">
                      <span>Unit Number</span>
                      <span>Make/Model</span>
                      <span>Status</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Importing Section */}
          {currentStep === 'importing' && (
            <div className="ru-importing-section">
              <div className="ru-importing-header">
                <div className="ru-importing-spinner" />
                <h2>Processing Your Files</h2>
                <p>Please wait while we import your data...</p>
              </div>
              <div className="ru-import-list">
                {queuedFiles.map((qf, index) => {
                  const typeInfo = getFileTypeInfo(qf.detectedType);
                  return (
                    <div key={index} className={`ru-import-item ${qf.status}`}>
                      <div className="ru-import-status">
                        {qf.status === 'pending' && (
                          <div className="ru-status-pending">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"/>
                            </svg>
                          </div>
                        )}
                        {qf.status === 'importing' && <div className="ru-status-spinner" />}
                        {qf.status === 'success' && (
                          <div className="ru-status-success">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          </div>
                        )}
                        {qf.status === 'error' && (
                          <div className="ru-status-error">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18"/>
                              <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="ru-import-info">
                        <span className="ru-import-name">{qf.file.name}</span>
                        <span className="ru-import-type" style={{ color: typeInfo.color }}>{typeInfo.label}</span>
                      </div>
                      {qf.status === 'success' && qf.result?.data && (
                        <div className="ru-import-result">
                          +{qf.result.data.created} created, {qf.result.data.updated} updated
                        </div>
                      )}
                      {qf.status === 'error' && (
                        <div className="ru-import-error">{qf.error || qf.result?.message}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Complete Section */}
          {currentStep === 'complete' && (
            <div className="ru-complete-section">
              <div className={`ru-complete-header ${errorCount > 0 ? 'has-errors' : ''}`}>
                <div className="ru-complete-icon">
                  {errorCount === 0 ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  )}
                </div>
                <h2>{errorCount === 0 ? 'Import Successful!' : 'Import Completed with Warnings'}</h2>
                <p>{errorCount === 0 ? 'All files have been processed successfully.' : `${errorCount} file(s) had issues during import.`}</p>
              </div>

              <div className="ru-results-grid">
                <div className="ru-result-card success">
                  <div className="ru-result-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                  </div>
                  <div className="ru-result-value">{successCount}</div>
                  <div className="ru-result-label">Files Imported</div>
                </div>
                <div className="ru-result-card created">
                  <div className="ru-result-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                  </div>
                  <div className="ru-result-value">{totalCreated.toLocaleString()}</div>
                  <div className="ru-result-label">Records Created</div>
                </div>
                <div className="ru-result-card updated">
                  <div className="ru-result-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3"/>
                    </svg>
                  </div>
                  <div className="ru-result-value">{totalUpdated.toLocaleString()}</div>
                  <div className="ru-result-label">Records Updated</div>
                </div>
                {errorCount > 0 && (
                  <div className="ru-result-card errors">
                    <div className="ru-result-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                      </svg>
                    </div>
                    <div className="ru-result-value">{errorCount}</div>
                    <div className="ru-result-label">Failed Files</div>
                  </div>
                )}
              </div>

              <div className="ru-file-details">
                <h4>Import Details</h4>
                {queuedFiles.map((qf, index) => {
                  const typeInfo = getFileTypeInfo(qf.result?.fileType || qf.detectedType);
                  return (
                    <div key={index} className={`ru-detail-item ${qf.status}`}>
                      <div className="ru-detail-header">
                        <div className="ru-detail-status">
                          {qf.status === 'success' ? (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18"/>
                              <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          )}
                        </div>
                        <div className="ru-detail-info">
                          <span className="ru-detail-name">{qf.file.name}</span>
                          <span className="ru-detail-type" style={{ color: typeInfo.color }}>{typeInfo.label}</span>
                        </div>
                      </div>
                      {qf.status === 'success' && qf.result?.data && (
                        <div className="ru-detail-stats">
                          <span className="created">{qf.result.data.created} created</span>
                          <span className="updated">{qf.result.data.updated} updated</span>
                          <span className="failed">{qf.result.data.failed} failed</span>
                          {qf.result.data.warnings && qf.result.data.warnings.length > 0 && (
                            <span className="warnings">{qf.result.data.warnings.length} warnings</span>
                          )}
                        </div>
                      )}
                      {qf.status === 'error' && (
                        <div className="ru-detail-error">{qf.error || qf.result?.message || 'Unknown error'}</div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="ru-complete-actions">
                <button className="ru-btn ru-btn-primary" onClick={handleReset}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Import More Files
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default RentalUtilizationImportPage;
